import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { MenuOptionValue, NestedPick, Paginated, PaginationQueryWithFields } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { ClaimType, UpsertClaimType } from "./types";

export const useClaimTypesApi = () => {
  const api = useApi();

  const getClaimType = useCallback(
    (claimTypeId: string, signal: AbortSignal) => {
      const url = `/admin/claim_types/${claimTypeId}`;
      return useSWRMutation<ClaimType | null, CustomError, string>(url, (url) => api.get<ClaimType>({ url, signal }));
    },
    [api],
  );

  const useAdminClaimTypes = useCallback(
    <K extends keyof ClaimType>(query: PaginationQueryWithFields<NestedPick<ClaimType, K>>, signal: AbortSignal) => {
      const url = buildUrl<NestedPick<ClaimType, K>>("/admin/claim_types", query);
      return useSWR<Paginated<NestedPick<ClaimType, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<ClaimType, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminClaimType = useCallback(
    async (body: UpsertClaimType): Promise<{ id: string }> => {
      const url = `/admin/claim_types`;
      const id = await api.post<UpsertClaimType, { id: string }>(url, body);
      api.mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminClaimType = useCallback(
    async (id: string, body: UpsertClaimType) => {
      const url = `/admin/claim_types/${id}`;
      await api.put<UpsertClaimType>(url, body);
      api.mutateCache([`/admin/claims`, `/admin/claim_types`, `/admin/claim_types/${id}/versions`]);
    },
    [api],
  );

  const deleteAdminClaimType = useCallback(
    async (claimTypeId: string): Promise<void> => {
      const url = `/admin/claim_types/${claimTypeId}`;
      await api.del(url);
      api.mutateCache([`/admin/claim_types`]);
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/claim_types/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  return {
    deleteAdminClaimType,
    updateAdminClaimType,
    insertAdminClaimType,
    getClaimType,
    useAdminClaimTypes,
    getFieldFilters,
  };
};
